'use client';

import { usePathname, useRouter } from 'next/navigation';
import SegmentedToggle from '@/components/SegmentedToggle';
import { ToggleOption } from '@/interfaces';
import type { Language } from '@/lib/translations';

interface LanguageSwitcherProps {
  language?: Language;
}

export default function LanguageSwitcher({
  language,
}: LanguageSwitcherProps = {}) {
  const pathname = usePathname();
  const router = useRouter();

  const localeMatch = pathname.match(/^\/(en|zh)(.*)$/);
  const derivedLang = (localeMatch?.[1] as Language) ?? 'zh';
  const suffix = localeMatch?.[2] ?? pathname ?? '/';
  const pagePath =
    suffix.length === 0 ? '/' : suffix.startsWith('/') ? suffix : `/${suffix}`;

  const currentLang = language ?? derivedLang;

  const options: ToggleOption[] = [
    {
      value: 'en',
      label: 'EN',
      ariaLabel: 'English',
    },
    {
      value: 'zh',
      label: '中文',
      ariaLabel: 'Chinese',
    },
  ];

  const handleLanguageChange = (lang: string) => {
    if (lang === currentLang) return;
    const newUrl =
      lang === 'en'
        ? pagePath === '/'
          ? '/en/'
          : `/en${pagePath}`
        : pagePath === '/'
          ? '/zh/'
          : `/zh${pagePath}`;
    router.push(newUrl);
  };

  return (
    <SegmentedToggle
      options={options}
      value={currentLang}
      onChange={handleLanguageChange}
    />
  );
}
